import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole, getUserRoleString } from '@/types/api';
import { Spinner } from '@/components/Loading';

// Protected Route Props
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  fallback?: React.ReactNode;
  redirectTo?: string;
}

/**
 * Protected Route Component
 * 
 * Wraps components that require authentication.
 * Optionally checks for specific roles.
 * Redirects to login or shows fallback if unauthorized.
 */
export function ProtectedRoute({ 
  children, 
  requiredRoles, 
  fallback,
  redirectTo = '/login' 
}: ProtectedRouteProps) {
  const { isAuthenticated, isInitialized, hasAnyRole, user } = useAuth();
  const location = useLocation();

  // Show loading while auth state is initializing
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate 
        to={redirectTo} 
        state={{ from: location }}
        replace 
      />
    );
  }

  // Check role requirements if specified
  if (requiredRoles && requiredRoles.length > 0) {
    console.log('ProtectedRoute - Checking roles:', {
      requiredRoles,
      userRole: user?.role,
      userRoleType: typeof user?.role,
      hasAnyRoleResult: hasAnyRole(requiredRoles)
    });
    
    if (!hasAnyRole(requiredRoles)) {
      // Show fallback or access denied message
      if (fallback) {
        return <>{fallback}</>;
      }
      
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="max-w-md mx-auto text-center">
            <div className="mb-6">
              <div className="mx-auto h-12 w-12 text-red-400">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-600 mb-6">
              You don't have permission to access this page.
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Required roles: {requiredRoles.map(role => getUserRoleString(role)).join(', ')}
            </p>
            <p className="text-sm text-gray-500">
              Your role: {user?.role !== undefined ? getUserRoleString(user.role) : 'Unknown'}
            </p>
            <div className="mt-6">
              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // All checks passed, render children
  return <>{children}</>;
}

// Convenience components for specific roles
export function AdminRoute({ children, ...props }: Omit<ProtectedRouteProps, 'requiredRoles'>) {
  return (
    <ProtectedRoute requiredRoles={[UserRole.Admin]} {...props}>
      {children}
    </ProtectedRoute>
  );
}

export function AgentRoute({ children, ...props }: Omit<ProtectedRouteProps, 'requiredRoles'>) {
  return (
    <ProtectedRoute requiredRoles={[UserRole.Admin, UserRole.Manager, UserRole.Agent]} {...props}>
      {children}
    </ProtectedRoute>
  );
}

export function EndUserRoute({ children, ...props }: Omit<ProtectedRouteProps, 'requiredRoles'>) {
  return (
    <ProtectedRoute requiredRoles={[UserRole.EndUser]} {...props}>
      {children}
    </ProtectedRoute>
  );
}

// Public Route - redirects authenticated users away
interface PublicRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * Public Route Component
 * 
 * For pages that should only be accessible to non-authenticated users
 * (like login, register pages). Redirects authenticated users.
 */
export function PublicRoute({ children, redirectTo = '/dashboard' }: PublicRouteProps) {
  const { isAuthenticated, isInitialized } = useAuth();
  const location = useLocation();

  // Show loading while auth state is initializing
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect authenticated users
  if (isAuthenticated) {
    // Try to redirect to the page they were trying to access before login
    const from = location.state?.from?.pathname || redirectTo;
    return <Navigate to={from} replace />;
  }

  // Not authenticated, show the public page
  return <>{children}</>;
}

// Layout wrapper for authenticated pages
interface AuthenticatedLayoutProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
}

export function AuthenticatedLayout({ children, requiredRoles }: AuthenticatedLayoutProps) {
  return (
    <ProtectedRoute requiredRoles={requiredRoles}>
      {children}
    </ProtectedRoute>
  );
}

// Role-based conditional rendering hook
export function useRoleBasedAccess() {
  const { hasRole, hasAnyRole, isAdmin, isAgent, isEndUser } = useAuth();

  const canAccess = (roles: UserRole[]) => hasAnyRole(roles);
  
  const canAccessAdmin = () => isAdmin();
  const canAccessAgent = () => isAgent();
  const canAccessEndUser = () => isEndUser();

  return {
    hasRole,
    hasAnyRole,
    canAccess,
    canAccessAdmin,
    canAccessAgent, 
    canAccessEndUser,
    isAdmin,
    isAgent,
    isEndUser,
  };
}